import { type ComponentProps, type ReactNode, type SVGProps } from 'preact/compat';

import { type SvgImage } from '@/shared/types/common';

export type AnyIcon = string | SvgImage | ReactNode;

type Props<T extends AnyIcon> = (T extends string
  ? ComponentProps<'img'>
  : T extends SvgImage
    ? SVGProps<SVGSVGElement>
    : never) & {
  icon: T | null | undefined;
};

const isImgProps = (
  props: Props<AnyIcon>
): props is ComponentProps<'img'> & {
  icon: string;
} => {
  return !!props.icon && typeof props.icon === 'string';
};

const isSvgProps = (
  props: Props<AnyIcon>
): props is SVGProps<SVGSVGElement> & {
  icon: SvgImage;
} => {
  return !!props.icon && typeof props.icon === 'function';
};

const IconInner = <T extends AnyIcon>(props: Props<T>) => {
  if (!props.icon) return null;

  if (isImgProps(props)) {
    const { icon, ...imgProps } = props;

    return <img src={decodeURIComponent(icon)} {...imgProps} />;
  }

  if (isSvgProps(props)) {
    const { icon, ...svgProps } = props;

    const IconToRender = icon as SvgImage;

    return <IconToRender {...svgProps} />;
  }

  return props.icon as ReactNode;
};

export const Icon = IconInner as typeof IconInner;
